/**
 * How the company profile is rendered as text, and where its logo is fetched
 * from.
 *
 * Lives apart from the settings screen because the invoices have to print the
 * same address the same way. Two components each doing "line1, line2, city
 * postcode" by hand is how the order invoice and the fabric invoice ended up
 * disagreeing in the first place.
 */

/**
 * What an invoice prints when the profile has not arrived yet.
 *
 * These are the values the components hardcoded before this feature, and they
 * mirror the API's CompanyDefaults. This is a safety net for one case only —
 * the profile is still in flight, or the request failed — because a PDF that
 * renders with a blank letterhead is worse than one that renders last known
 * good. It is not consulted once `company` is present, and an install that has
 * edited its profile never sees these values again.
 */
export const COMPANY_FALLBACK = {
  name: 'Elegant Tex',
  addressLine1: 'House 52, Lane 2',
  addressLine2: 'Block C, Ave No. 5',
  city: 'Dhaka',
  postcode: '1216',
  country: 'Bangladesh',
  phone: '+8801896224057',
  email: 'bd.eleganttex@gmail.com',
  invoiceFooter: 'Computer generated invoice, valid without seal.',
  logoUrl: null,
};

/** The profile, or the fallback if it has not loaded. Never null. */
export const withFallback = (company) =>
  company && company.name ? company : COMPANY_FALLBACK;

/** Where the shell mirrors the profile between visits. */
export const COMPANY_STORAGE_KEY = "COMPANY_PROFILE";

/**
 * The last profile the shell saw, or null. Shared by ContextProvider (which
 * writes it) and the guest screens (which only read it), so the key and the
 * "a corrupted entry must not take the app down on boot" rule live in one place.
 */
export const readStoredCompany = () => {
  try {
    const raw = localStorage.getItem(COMPANY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * The monogram for a company with no uploaded logo: one letter per word, at
 * most two. "Elegant Tex" → "ET", which is what the sidebar drew as a literal
 * before the profile existed.
 */
export const initialsOf = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Where the logo is fetched from, or null when none is set.
 *
 * Built from REACT_APP_API_BASE_URL rather than taken from the payload's
 * `logoUrl`, for the same reason every other image in this app is: the API's
 * own absolute URL comes from APP_URL, which is not always the host the SPA is
 * talking to. `logoUrl` still tells us whether a logo exists at all, and the
 * `v` stamp is what makes the endpoint's immutable cache header safe.
 */
export const logoSrc = (company) =>
  company?.logoUrl
    ? `${process.env.REACT_APP_API_BASE_URL}/company/logo?v=${encodeURIComponent(company.updatedAt || '')}`
    : null;

/**
 * The address as an invoice prints it: one line per populated part, city and
 * postcode joined, and nothing emitted for a field the company left blank — no
 * stray comma, no empty line, no "undefined".
 *
 * @param {object|null|undefined} company
 * @returns {string[]}
 */
export const addressLines = (company) => [
  company?.addressLine1,
  company?.addressLine2,
  [company?.city, company?.postcode].filter(Boolean).join(' '),
  company?.country,
]
  .map((line) => (line == null ? '' : String(line).trim()))
  .filter((line) => line !== '');

/**
 * The contact lines that sit under the address — phone(s) then email. Blank
 * fields disappear rather than printing an empty row.
 *
 * @param {object|null|undefined} company
 * @returns {string[]}
 */
export const contactLines = (company) => [
  [company?.phone, company?.altPhone].filter(Boolean).join(', '),
  company?.email,
]
  .map((line) => (line == null ? '' : String(line).trim()))
  .filter((line) => line !== '');
